import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import CloseIcon from "@mui/icons-material/Close";
import { getBillPrintHistory } from "../../services";
import { BillPrintRow } from "../../types/reprintAuth.type";
import { formatDate } from "../../utils/date";
import { getCopySetLabel, CHARCOAL } from "./data";

interface PrintHistoryModalProps {
  open: boolean;
  transactionUuid: string | null;
  transactionNo?: string;
  onClose: () => void;
}

interface Tile {
  label: string;
  value: number;
  color: string;
}

export default function PrintHistoryModal({
  open,
  transactionUuid,
  transactionNo,
  onClose,
}: PrintHistoryModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<BillPrintRow[]>([]);

  const fetchHistory = useCallback(async () => {
    if (!transactionUuid) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getBillPrintHistory(transactionUuid);
      setRows((data.rows ?? []) as BillPrintRow[]);
    } finally {
      setLoading(false);
    }
  }, [transactionUuid]);

  useEffect(() => {
    if (open && transactionUuid) fetchHistory();
    if (!open) setRows([]);
  }, [open, transactionUuid, fetchHistory]);

  const total = rows.length;
  const originalCount = rows.filter((r) => r.print_type === "ORIGINAL").length;
  const reprintCount = rows.filter((r) => r.print_type === "REPRINT").length;

  const tiles: Tile[] = [
    { label: "พิมพ์ทั้งหมด", value: total, color: CHARCOAL },
    { label: "ต้นฉบับ", value: originalCount, color: "#45be77" },
    { label: "พิมพ์ซ้ำ", value: reprintCount, color: "#FDA42C" },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, color: CHARCOAL, pr: 6 }}>
        ประวัติการพิมพ์บิล
        {transactionNo ? (
          <Typography variant="caption" display="block" color="text.secondary">
            {transactionNo}
          </Typography>
        ) : null}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={2} mb={2}>
          {tiles.map((tile) => (
            <Box
              key={tile.label}
              sx={{
                flex: 1,
                textAlign: "center",
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#f6f6f9",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: tile.color }}>
                {loading ? "-" : tile.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tile.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
              <TableRow>
                <TableCell>ประเภท</TableCell>
                <TableCell>ชุดสำเนา</TableCell>
                <TableCell>พิมพ์โดย</TableCell>
                <TableCell>เวลา</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((__, c) => (
                      <TableCell key={c}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" variant="body2">
                      ยังไม่มีการพิมพ์บิลนี้
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.uuid}>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          row.print_type === "ORIGINAL" ? "ต้นฉบับ" : "พิมพ์ซ้ำ"
                        }
                        color={
                          row.print_type === "ORIGINAL" ? "success" : "warning"
                        }
                      />
                    </TableCell>
                    <TableCell>{getCopySetLabel(row.copy_set)}</TableCell>
                    <TableCell>{row.printed_by || "-"}</TableCell>
                    <TableCell>
                      {row.printed_at
                        ? formatDate(row.printed_at, "DD/MM/YYYY HH:mm")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
